// Centralized Google Maps JS API loader.
//
// Every consumer shares this single module-level promise so the script tag is
// injected exactly once. Previously each component ran its own
// `document.querySelector("#google-maps")` check and, if it lost the race, took
// the "already present" branch while the script was still downloading, leaving
// its AutocompleteService/PlacesService refs permanently null.

export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SCRIPT_ID = "google-maps";

let loadPromise = null;

const isPlacesReady = () =>
  typeof window !== "undefined" &&
  !!window.google &&
  !!window.google.maps &&
  !!window.google.maps.places;

/**
 * Loads the Google Maps JS API (with the `places` library) once.
 * @returns {Promise<void>} resolves when `window.google.maps.places` is usable.
 */
export const loadGoogleMaps = () => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (isPlacesReady()) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    const message =
      "REACT_APP_GOOGLE_MAPS_API_KEY is not set. Location lookup is disabled.";
    console.error(message);
    loadPromise = Promise.reject(new Error(message));
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      if (isPlacesReady()) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error("Failed to load the Google Maps JS API script."))
        );
      }
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    // `loading=async` is required by the modern bootstrap and silences the
    // "loaded directly without loading=async" performance warning.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&loading=async&v=weekly`;
    script.onload = () => {
      if (isPlacesReady()) {
        resolve();
      } else {
        reject(
          new Error(
            "Google Maps JS API loaded but the places library is unavailable."
          )
        );
      }
    };
    script.onerror = () =>
      reject(new Error("Failed to load the Google Maps JS API script."));

    const head = document.querySelector("head");
    if (!head) {
      reject(new Error("Cannot inject the Google Maps script: no <head>."));
      return;
    }
    head.appendChild(script);
  });

  loadPromise.catch((error) => {
    // Allow a later attempt to retry instead of caching the rejection forever.
    loadPromise = null;
    console.error("Google Maps loader error:", error);
  });

  return loadPromise;
};

/**
 * Loads the `places` library via importLibrary (Places API "New").
 *
 * google.maps.places.AutocompleteService / PlacesService are NOT available to
 * customers onboarded after March 1 2025 - constructing them yields a stub that
 * reports SERVICE_UNAVAILABLE. AutocompleteSuggestion / Place replace them.
 */
export const getPlacesLibrary = async () => {
  await loadGoogleMaps();
  if (!window.google?.maps?.importLibrary) {
    throw new Error("Google Maps importLibrary is unavailable.");
  }
  return window.google.maps.importLibrary("places");
};

/**
 * Fetches autocomplete predictions using the new AutocompleteSuggestion API and
 * maps them back to the legacy prediction shape the UI already renders:
 * `{ description, place_id, structured_formatting: { main_text,
 *    main_text_matched_substrings, secondary_text } }`.
 *
 * @param {string} input the raw user input
 * @param {object} [options]
 * @param {object} [options.sessionToken] an AutocompleteSessionToken
 * @returns {Promise<Array<object>>} legacy-shaped predictions
 */
export const fetchAutocompletePredictions = async (input, options = {}) => {
  const { AutocompleteSuggestion } = await getPlacesLibrary();

  if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    throw new Error(
      "google.maps.places.AutocompleteSuggestion is unavailable. Ensure the Places API (New) is enabled for this key."
    );
  }

  const request = { input };
  if (options.sessionToken) {
    request.sessionToken = options.sessionToken;
  }

  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return (suggestions || [])
    .map((suggestion) => suggestion.placePrediction)
    .filter(Boolean)
    .map((prediction) => {
      const mainText = prediction.mainText;
      const secondaryText = prediction.secondaryText;

      return {
        description: prediction.text?.toString() || "",
        place_id: prediction.placeId,
        structured_formatting: {
          main_text: mainText?.toString() || prediction.text?.toString() || "",
          // StringRange uses startOffset/endOffset; the legacy renderer expects
          // offset/length. Note the API omits startOffset entirely when it is
          // 0, so it must be defaulted or the length becomes NaN.
          main_text_matched_substrings: (mainText?.matches || []).map(
            (match) => {
              const start = match.startOffset || 0;
              const end = match.endOffset || 0;
              return { offset: start, length: Math.max(end - start, 0) };
            }
          ),
          secondary_text: secondaryText?.toString() || "",
        },
        // Retained so callers can fetch details without re-wrapping.
        placePrediction: prediction,
      };
    });
};

/**
 * Creates a new AutocompleteSessionToken, which groups an autocomplete
 * keystroke session with its follow-up details request for billing.
 */
export const createSessionToken = async () => {
  const { AutocompleteSessionToken } = await getPlacesLibrary();
  return AutocompleteSessionToken ? new AutocompleteSessionToken() : null;
};

/**
 * Fetches details for a place using the new Place class.
 *
 * @param {string} placeId
 * @param {object} [prediction] a legacy-shaped prediction from
 *   fetchAutocompletePredictions; when present its placePrediction is reused so
 *   the autocomplete session token is applied automatically.
 * @returns {Promise<object>} the resolved google.maps.places.Place
 */
export const fetchPlaceDetailsNew = async (placeId, prediction) => {
  const { Place } = await getPlacesLibrary();

  if (!Place) {
    throw new Error(
      "google.maps.places.Place is unavailable. Ensure the Places API (New) is enabled for this key."
    );
  }

  const place = prediction?.placePrediction?.toPlace
    ? prediction.placePrediction.toPlace()
    : new Place({ id: placeId });

  await place.fetchFields({
    fields: [
      "displayName",
      "formattedAddress",
      "location",
      "addressComponents",
      "types",
    ],
  });

  return place;
};

/**
 * Normalizes a Places (New) `Place` into the fields the app persists.
 *
 * Mirrors the previous legacy behaviour:
 *  - `label` is the place name, but only for point_of_interest establishments.
 *  - locality/region previously came from parsing the `adr_address` microformat;
 *    they are now read straight from addressComponents (region uses the short
 *    form, e.g. "CA", which is what the adr `.region` span contained).
 *
 * @param {object} place a Place with fetched fields
 * @returns {{ locationDisplay: string, locationCountry: string, locality: string,
 *   region: string, latitude: number, longitude: number, name: string }}
 */
export const normalizePlaceDetails = (place) => {
  const components = place.addressComponents || [];
  const types = place.types || [];

  let locality = "";
  let region = "";
  let country = "";

  components.forEach((component) => {
    const componentTypes = component.types || [];
    if (componentTypes.includes("locality")) {
      locality = component.longText || "";
    }
    if (componentTypes.includes("administrative_area_level_1")) {
      region = component.shortText || component.longText || "";
    }
    if (componentTypes.includes("country")) {
      country = component.longText || "";
    }
  });

  const name =
    typeof place.displayName === "string"
      ? place.displayName
      : place.displayName?.text || "";

  const isEstablishment =
    types.includes("point_of_interest") && types.includes("establishment");
  const label = isEstablishment ? name : "";

  const locationDisplay =
    label && locality && region
      ? `${label}, ${locality}, ${region}`
      : locality && region
      ? `${locality}, ${region}`
      : "";

  // Place.location is a LatLng; lat/lng are methods on it.
  const latitude =
    typeof place.location?.lat === "function"
      ? place.location.lat()
      : place.location?.lat;
  const longitude =
    typeof place.location?.lng === "function"
      ? place.location.lng()
      : place.location?.lng;

  return {
    locationDisplay,
    locationCountry: country,
    locality,
    region,
    latitude,
    longitude,
    name,
  };
};

/**
 * Human-readable explanation for a legacy Places service status code.
 * @param {string} status a google.maps.places.PlacesServiceStatus value
 */
export const describePlacesStatus = (status) => {
  switch (status) {
    case "REQUEST_DENIED":
      return "Google rejected the request. Check that the API key is valid, that the Places API is enabled, and that this domain is an allowed referrer.";
    case "OVER_QUERY_LIMIT":
      return "The Google Maps quota for this key has been exceeded, or billing is not enabled.";
    case "INVALID_REQUEST":
      return "The request sent to Google Places was invalid.";
    case "ZERO_RESULTS":
      return "Google Places returned no matches for this input.";
    case "NOT_FOUND":
      return "Google Places could not find the referenced place.";
    case "UNKNOWN_ERROR":
      return "Google Places had a temporary server error. Retrying may help.";
    default:
      return `Google Places returned status "${status}".`;
  }
};

export default loadGoogleMaps;
